import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { IMG } from "@/data/content";
import { Reveal, ChapterLabel, PageHero, ImageReveal } from "@/components/Shared";

export const FaqList = ({ items, testPrefix }) => (
  <dl className="mt-10 max-w-3xl divide-y divide-white/10 border-y border-white/10">
    {items.map((f, i) => (
      <Reveal key={f.q} delay={i * 0.06}>
        <div className="py-7" data-testid={`${testPrefix}-faq-${i}`}>
          <dt>
            <h3 className="font-serif text-xl font-light text-[#F9F8F6] sm:text-2xl">{f.q}</h3>
          </dt>
          <dd className="mt-3 max-w-2xl font-sans text-sm leading-loose text-[#A1A1AA]">{f.a}</dd>
        </div>
      </Reveal>
    ))}
  </dl>
);

const Cta = ({ to, testid, children }) => (
  <Link
    to={to}
    data-testid={testid}
    data-cursor="OPEN"
    className="group inline-flex items-center gap-4 border border-[#C5A059] px-10 py-4 font-sans text-xs font-medium uppercase tracking-[0.3em] text-[#C5A059] transition-all duration-500 hover:bg-[#C5A059] hover:text-[#0B0B0C]"
  >
    {children}
    <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
  </Link>
);

const LessonsPage = () => {
  const { t, lp } = useLang();
  const L = t.lessons;
  return (
    <div data-testid="lessons-page">
      <PageHero eyebrow={L.eyebrow} title={L.title} img={IMG.coaching} imgClass="object-[65%_25%]">
        <p className="max-w-xl font-sans text-sm leading-loose text-[#A1A1AA]">{L.intro}</p>
      </PageHero>

      <section className="px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal><ChapterLabel no="01" label={L.focusLabel} /></Reveal>
        <div className="mt-12 grid gap-10 md:grid-cols-2">
          {L.focus.map((f, i) => (
            <Reveal key={f.t} delay={i * 0.1}>
              <div className="border-t border-[#C5A059]/30 pt-6">
                <span className="font-mono text-[10px] tracking-[0.35em] text-[#C5A059]">0{i + 1}</span>
                <h3 className="mt-3 font-serif text-2xl font-light text-[#F9F8F6] sm:text-3xl">{f.t}</h3>
                <p className="mt-3 max-w-md font-sans text-sm leading-loose text-[#A1A1AA]">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-white/5 bg-[#141416] px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal><ImageReveal src="/images/coaching-portrait.webp" alt="Angela Puxi – Sax-Coaching" className="aspect-[2/3] w-full" imgClass="object-top" /></Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal><ChapterLabel no="02" label={L.forLabel} /></Reveal>
            <Reveal delay={0.1}><p className="mt-8 font-sans text-sm leading-loose text-[#A1A1AA]">{L.forText}</p></Reveal>
            <Reveal delay={0.15} className="mt-8"><Cta to={`${lp("/contact")}?category=lessons`} testid="lessons-cta">{L.cta}</Cta></Reveal>
            <Reveal className="mt-16"><ChapterLabel no="03" label={L.workshopLabel} /></Reveal>
            <Reveal delay={0.1}><p className="mt-8 font-sans text-sm leading-loose text-[#A1A1AA]">{L.workshopText}</p></Reveal>
            <Reveal delay={0.15} className="mt-8"><Cta to={`${lp("/contact")}?category=workshop`} testid="workshop-cta">{L.ctaWorkshop}</Cta></Reveal>
          </div>
        </div>
      </section>

      <section className="px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal><ChapterLabel no="04" label={L.faqLabel} /></Reveal>
        <FaqList items={L.faq} testPrefix="lessons" />
        <Reveal delay={0.2} className="mt-12">
          <p className="font-sans text-sm text-[#71717A]">
            <Link to={lp("/equipment")} className="underline underline-offset-4 hover:text-[#C5A059]">{t.nav.equipment}</Link> · <Link to={lp("/biography")} className="underline underline-offset-4 hover:text-[#C5A059]">{t.nav.biography}</Link> · <Link to={lp("/bands/events")} className="underline underline-offset-4 hover:text-[#C5A059]">{t.nav.events}</Link>
          </p>
        </Reveal>
      </section>
    </div>
  );
};

export default LessonsPage;
